/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.21.0
 * @since 1.21.0
 */

import { useCallback, useEffect } from 'react';

import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { utoa } from '@/main/utils/URIEncode';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { useSetTableFilterAndSortMutation } from '@modules/table/store';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';

import {
    CampaignsTableAccountInitialise,
    CampaignsTableSubscriberInitialise,
} from '../components/Table/CampaignsTableInitialise';
import {
    useLazyGetAccountCountQuery,
    useLazyGetAccountTotalCountQuery,
    useLazyGetSubscriberCountQuery,
    useLazyGetSubscriberTotalCountQuery,
} from '../store';
import { ECampaignsType, ICampaignsRouteFilter } from '../types';


export const useCheckOnCampaignCount = () => {

    const location = useLocation<{ filter: ICampaignsRouteFilter }>();
    const history = useHistory();
    const isCampaignsImprovementsFeature = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CAMPAIGNS_IMPROVEMENT));

    const [getAccountListCount, { data: accountCount }] = useLazyGetAccountCountQuery();
    const [getSubscriberListCount, { data: subscriberCount }] = useLazyGetSubscriberCountQuery();
    const [getAccountTotalCount, { data: accountTotalCount }] = useLazyGetAccountTotalCountQuery();
    const [getSubscriberTotalCount, { data: subscriberTotalCount }] = useLazyGetSubscriberTotalCountQuery();

    const [filter] = useSetTableFilterAndSortMutation();

    const clearLocationState = useCallback(() => {
        history.replace({ ...history.location, state: undefined });
    }, [history]);

    const checkOnCampaignCount = useCallback(() => {
        if (isCampaignsImprovementsFeature) {
            //TODO переделать использование промисов на более современный подход
            Promise.all([
                getAccountTotalCount().unwrap(),
                getSubscriberTotalCount().unwrap(),
            ]).then(() => {
                clearLocationState();
            })
        } else {
            Promise.all([
                getAccountListCount({ from: 0, size: 1 }).unwrap(),
                getSubscriberListCount({ from: 0, size: 1 }).unwrap(),
            ]).then(() => {
                clearLocationState();
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, clearLocationState, isCampaignsImprovementsFeature]);

    useEffect(() => {
        /* Предварительная фильтрация */
        if (location.state?.filter) {
            const value = utoa(location.state.filter.entityId.toString());
            const type = location.state.filter.type;

            const config = {
                [ECampaignsType.account]: CampaignsTableAccountInitialise,
                [ECampaignsType.subscriber]: CampaignsTableSubscriberInitialise,
            }[type];

            const filterValue = {
                [ECampaignsType.account]: {
                    clientId: { value },
                },
                [ECampaignsType.subscriber]: {
                    msisdn: { value },
                },
            }[type];

            config.filterAndSort && filter({ url: config.filterAndSort.url, filter: filterValue }).then(() => {
                checkOnCampaignCount();
            });
        } else {
            checkOnCampaignCount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.filter]);

    if (isCampaignsImprovementsFeature) {
        return {
            accountCount: accountTotalCount || 0,
            subscriberCount: subscriberTotalCount || 0,
        }
    }

    return {
        accountCount: accountCount || 0,
        subscriberCount: subscriberCount || 0,
    };
};
